import Vue from 'vue';
import Component from 'vue-class-component';
import {AXIOS} from "@/lib/http-common";
import UserActionResultSender from "@/components/application/UserActionResultSender";
import UserField from "@/components/configuration/model/UserField";
import {FieldDataType} from "@/components/configuration/model/FieldDataType";
import {USER_FIELDS} from "@/lib/constants";
import {i18n} from "@/main";

@Component({
  computed: {
    USER_FIELDS() {
      return USER_FIELDS;
    },
    FieldDataType() {
      return FieldDataType;
    }
  }
})
export default class CustomArticleFieldsView extends Vue {
  public userFields: UserField[] = [];
  public userFieldToEdit: UserField = this.getInitializedUserField();
  public loading: boolean = false;
  public isRequired = [(value: string) => {
    if (value) {
      return true;
    }
    return i18n.tc('global.rules.required');
  }];
  public isNumeric = [(value: string) => {
    if (value == null) {
      return true;
    }
    if (((value !== '') && !isNaN(Number(value.toString())))) {
      return true;
    }

    return i18n.tc('global.rules.valueMustBeANumber');
  }];

  public isBoolean = [(value: string) => {
    if (value === '' || value === null || value === undefined) { return true; }
    return ['true', 'false'].includes(value.toLowerCase()) || 'Value must be "true" or "false"';
  }];

  public existsOrTooBig = [(value: string) => {
    if (value.length > 35) {
      return i18n.tc('global.rules.invalidSize');
    }

    if (this.userFieldToEdit.dropDownValues.filter(dropdownValue => dropdownValue === value).length > 0) {
      return i18n.tc('global.rules.exists');
    }

    return true;
  }];

  public atLeastOneDropdownValueExists = [(value: string) => {
    if (this.userFieldToEdit.fieldDataType !== FieldDataType.DROPDOWN) {
      return true;
    }

    if (this.userFieldToEdit.dropDownValues.length === 0) {
      return i18n.tc('global.rules.thereShouldBeAtLeastOneDropdownOption');
    }

    return true;
  }];
  public editUserFieldDialogOpened: boolean = false;
  public isEditUserFieldFormValid: any = null;
  public isDropdownOptionBeingEditedFormValid: any = null;
  public isNewDropDownFormValid: any = null;
  public editUserFieldDropdownOptionsOpened: boolean = true;
  public editUserFieldNewDropdownOption: string = '';
  public dropdownOptionBeingEdited: string = '';
  public dropdownOptionBeingEditedInitial: string = '';
  public search: string = '';

  get userFieldsTH() {
    return [
      {text: this.$t('userFields.tableHeaders.fieldName'), value: "fieldName"},
      {text: this.$t('userFields.tableHeaders.mandatory'), value: "mandatory"},
      {text: this.$t('userFields.tableHeaders.defaultValue'), value: "defaultValue"},
      {text: this.$t('userFields.tableHeaders.fieldDataType'), value: "fieldDataType"},
      {text: this.$t('userFields.tableHeaders.dropDownValues'), value: "dropDownValues"},
      {text: this.$t('userFields.tableHeaders.enabled'), value: "enabled"},
      {text: this.$t('userFields.tableHeaders.action'), value: "action", sortable: false}
    ];
  }

  public mounted() {
    this.getUserFieldsFromBackend();
  }

  public getUserFieldsFromBackend() {
    this.loading = true;
    AXIOS.get("/user-field/")
      .then((message) => {
        this.userFields = message.data;
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public getInitializedUserField(): UserField {
    return {
      id: '',
      fieldId: '',
      fieldName: '',
      enabled: false,
      mandatory: false,
      defaultValue: '',
      currentValue: '',
      fieldDataType: FieldDataType.ANY_CHARACTER,
      dropDownValues: []
    };
  }

  public cleanupEditUserField(userField: UserField) {
    userField.id = '';
    userField.fieldId = '';
    userField.fieldName = '';
    userField.enabled = false;
    userField.mandatory = false;
    userField.defaultValue = '';
    userField.currentValue = '';
    userField.fieldDataType = FieldDataType.ANY_CHARACTER;
    userField.dropDownValues = userField.dropDownValues.splice(0, userField.dropDownValues.length);
  }

  public openEditUserFieldDialog(userField: UserField) {
    this.editUserFieldDialogOpened = true;
    this.userFieldToEdit.id = userField.id;
    this.userFieldToEdit.fieldName = userField.fieldName;
    this.userFieldToEdit.enabled = userField.enabled;
    this.userFieldToEdit.mandatory = userField.mandatory;
    this.userFieldToEdit.defaultValue = userField.defaultValue;
    this.userFieldToEdit.currentValue = userField.currentValue;
    this.userFieldToEdit.fieldDataType = userField.fieldDataType;
    this.userFieldToEdit.dropDownValues = [...userField.dropDownValues];
  }

  public closeEditUserFieldDialog() {
    this.editUserFieldDialogOpened = false;
    this.editUserFieldDropdownOptionsOpened = true;
    this.resetValidationEditForm();
    this.resetDropdownOptionBeingEditedForm();
    this.userFieldToEdit.dropDownValues.forEach(dropdownValue => {
      this.deleteNewDropdownOption(dropdownValue);
    });

    this.cleanupEditUserField(this.userFieldToEdit);
    this.getUserFieldsFromBackend();
    this.dropdownOptionBeingEdited = '';
    this.dropdownOptionBeingEditedInitial = '';
    this.editUserFieldNewDropdownOption = '';
  }

  public enableEditDropdownOption(editedOption: string) {
    this.dropdownOptionBeingEdited = editedOption;
    this.dropdownOptionBeingEditedInitial = editedOption;
  }

  public updateEditDropdownOption() {
    const index = this.userFieldToEdit.dropDownValues.findIndex(value => value === this.dropdownOptionBeingEdited);
    this.userFieldToEdit.dropDownValues[index] = this.dropdownOptionBeingEdited;
    this.dropdownOptionBeingEditedInitial = '';
    this.resetDropdownOptionBeingEditedForm();
  }

  public cancelEditDropdownOption() {
    this.dropdownOptionBeingEditedInitial = '';
    this.resetDropdownOptionBeingEditedForm();
  }

  public resetValidationEditForm() {
    (this.$refs.editUserFieldForm as Vue & { resetValidation: () => boolean }).resetValidation();
  }

  public validateEditForm() {
    (this.$refs.editUserFieldForm as Vue & { validate: () => boolean }).validate();
  }

  public resetDropdownOptionBeingEditedForm() {
    if (this.$refs.dropdownOptionBeingEditedForm) {
      (this.$refs.dropdownOptionBeingEditedForm as Vue & { resetValidation: () => boolean }).resetValidation();
    }
  }

  public editUserField() {
    this.loading = true;

    AXIOS.put(`/configuration/user-field`, this.userFieldToEdit)
      .then(() => {
        UserActionResultSender.showSuccessFeedback('userFields.success.userFieldEdited');
        this.closeEditUserFieldDialog();
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public addAnotherDropdownOption() {
    if (this.editUserFieldNewDropdownOption && this.editUserFieldNewDropdownOption.length > 0 && this.isNewDropDownFormValid) {
      this.userFieldToEdit.dropDownValues.push(this.editUserFieldNewDropdownOption);
      this.editUserFieldNewDropdownOption = '';
    }
    this.validateEditForm();
  }

  public deleteNewDropdownOption(option: string) {
    this.userFieldToEdit.dropDownValues = this.userFieldToEdit.dropDownValues.filter(value => option !== value);
  }
}
